import { useEffect, useState } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, useDisclosure, Textarea, Input } from "@nextui-org/react";
import { RootState, updateMessageFeedback, useAppDispatch, useUpdateFeedbackMutation, useUpdateGuestFeedbackMutation, useUpdateAgentFeedbackMutation, updateCommentHistory, updateAgentMessageState, getFeedbackDraft, saveFeedbackDraft, clearFeedbackDraft } from "@circuitry-ai/doc-data";
import { CtyShowToast, checkType, conversationHistoryTimezone, getCurrentDateTime, getUserInitials, useCommentAttachmentUpload, CommentAttachmentButton, AttachmentPreviewSection, AttachmentSourcesPreview } from "@circuitry-ai/doc-ui";
import { useTranslation } from 'react-i18next';
import { ChatLogo } from "./chatlogo";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';

import { useSelector } from "react-redux";

const  CommentModal = ({ data, onDialogClose, advisor_id, isCommentFromHistoryPage = false, getHistoryRefetch, chatAdvisor, msgIndex, session_id, isAgentFeedback = false }: any) => {
  const { t } = useTranslation();
  const [updateFeedback, { isLoading: isFeedbackLoading }] = useUpdateFeedbackMutation();
  const [updateGuestFeedback, { isLoading: isGuestFeedbackLoading }] = useUpdateGuestFeedbackMutation();
  const [updateAgentFeedback, { isLoading: isAgentFeedbackLoading }] = useUpdateAgentFeedbackMutation();
  const { currentActiveSocketObject } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { guestToken } = useSelector((state: RootState) => state.advisor);
  const messageId = isAgentFeedback
    ? (data?.messageId ?? '')
    : (data?.metaData?.find((item: any) => item?.message_id)?.message_id ?? '');
  const [currentUserComment, setUserComment] = useState<string>(() => getFeedbackDraft(messageId));
  const dispatch = useAppDispatch();
  /** Attachment upload is only offered for advisor chat/history comments, not agent live converse / agent history. */
  const { uploadedFiles, setUploadedFiles, addFiles, handlePaste, sources, isUploading, reset: resetAttachments } = useCommentAttachmentUpload();
  useEffect(() => { onOpen() }, []);
  const { sender_id = "" } = useParams<{ sender_id?: string, advisor_id?: string; }>();
  const decodedSenderId = window.atob(sender_id);
  const [userEmail, setUserEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [commentError, setCommentError] = useState<string>('');
  const { token, user } = useSelector((state: RootState) => state.auth);

  const hasAttachments = sources.length > 0;
  const isUpdateButtonDisabled = isFeedbackLoading || isGuestFeedbackLoading || isAgentFeedbackLoading || isUploading || (!currentUserComment.trim() && !hasAttachments) || (!token && !userEmail.trim());
  const isCommentFieldDisabled = (!token && !userEmail.trim());

  const closeModal = (persistDraft = true) => {
    if (persistDraft && currentUserComment.trim()) {
      saveFeedbackDraft(messageId, currentUserComment);
    }
    resetAttachments();
    onClose();
    onDialogClose();
  };

  const closeAfterSuccess = () => {
    clearFeedbackDraft(messageId);
    closeModal(false);
  };

  const getMessageTime = (time: string) => {
    if (isCommentFromHistoryPage || isAgentFeedback) {
      return conversationHistoryTimezone(time)
    }
    else{
      return time;
    }
  }

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError('');
    setCommentError('');

    if (!currentUserComment.trim() && !hasAttachments) {
      setCommentError('Comment is required');
      isValid = false;
    }

    if (!token && !userEmail) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!token && !userEmail.includes('@')) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    return isValid;
  };

  const saveMetadata = async () => {
    if (!validateForm()) {
      return;
    }

    if (isAgentFeedback) {
      // Agent feedback payload
      const agentFeedbackPayload = {
        feedback: 'comment',
        message_id: data?.messageId,
        ...(session_id ? { session_id } : {}),
        ...(advisor_id ? { aiworker_id: advisor_id } : {}),
        comment: currentUserComment
      };
      
      try {
        const response = await updateAgentFeedback(agentFeedbackPayload).unwrap();
        CtyShowToast(checkType.success, response?.message || 'Comment submitted successfully');
        
        // For agent conversations, use Redux agent message state
        const comment = {
          comment: currentUserComment,
          updated_at: getCurrentDateTime(new Date()),
          updated_by: advisor_id,
          feedback_type: 'comment',
          updated_by_details: {
            name: user?.details?.name,
            email: user?.details?.email
          },
          feedback_id: uuidv4()
        };
        dispatch(updateAgentMessageState({ 
          messageId: data?.messageId, 
          updates: { comment } 
        }));

        clearFeedbackDraft(messageId);
        closeAfterSuccess();
      } catch (error) {
        saveFeedbackDraft(messageId, currentUserComment);
        CtyShowToast(checkType.error, (error as any)?.data?.message || 'Failed to submit comment');
      }
    } else {
      const feedbackPayload = {
        feedback: 'comment',
        message_id: messageId,
        sender_id: isCommentFromHistoryPage ? (decodedSenderId ?? chatAdvisor?.sender_id) : (data?.metaData?.[2]?.session_id ?? currentActiveSocketObject?.id),
        comment: currentUserComment,
        advisor_id: advisor_id,
        ...(hasAttachments && { sources }),
        ...(!token && {
          email: userEmail
        })
      };
      try {
        const apiPayLoad = {
          payLoad: feedbackPayload,
          token: guestToken
        }
        const response = token ? await updateFeedback(feedbackPayload).unwrap() : await updateGuestFeedback(apiPayLoad).unwrap();

        CtyShowToast(checkType.success, response?.message);
        dispatch(updateMessageFeedback({ messageId: messageId, comment: currentUserComment }));

        if (isCommentFromHistoryPage) {
          getHistoryRefetch?.();
        } else {
          const comment = {
            comment: currentUserComment,
            updated_at: getCurrentDateTime(new Date()),
            updated_by: advisor_id,
            feedback_type: 'comment',
            updated_by_details: {
              name: user?.details?.name,
              email: user?.details?.email
            },
            feedback_id: uuidv4(),
            ...(hasAttachments && { sources }),
          }
          dispatch(updateCommentHistory({ msgIndex, comment }));
        }
        closeAfterSuccess();
      } catch (error) {
        saveFeedbackDraft(messageId, currentUserComment);
        CtyShowToast(checkType.error, (error as Error)?.message ?? 'Something went wrong!')
        closeModal();
      }
    }
  };

  const commentHistory = (commentData: any, index: number) => {
    const { name, email = '' } = commentData.updated_by_details;
    return (
      <div className="flex space-x-3 items-start pb-2 rounded-md" key={index}>
        <ChatLogo viewLogo={false} initials={getUserInitials(name)} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-default-700">
              {name === 'Guest' ? email : name?.split('.')[0] ?? 'You'}
            </span>
            <span className="text-xs text-default-500">{getMessageTime(commentData.updated_at)}</span>
          </div>
          {Array.isArray(commentData.sources) && commentData.sources.length > 0 && (
            <AttachmentSourcesPreview sources={commentData.sources} />
          )}
          {commentData.comment && <div className="mt-2">{commentData.comment}</div>}
        </div>
      </div>
    );
  };

  return (
    <Modal id="comment-modal" backdrop={"blur"} isOpen={isOpen} onClose={() => closeModal()} className="max-h-[80%] max-w-3xl sm:max-w-3xl md:max-w-4xl lg:max-w-4xl " isDismissable={false} isKeyboardDismissDisabled={true}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col">
              <span className="font-semibold text-lg">Add a comment</span>
              <span className="text-default-500" style={{ fontWeight: 400, fontSize: '0.95rem' }}>Leave a comment to help improve responses.</span>
            </ModalHeader>
            <ModalBody className="overflow-y-auto">
              <div className="flex flex-col gap-2">
                {(data?.comment_history ?? []).length > 0 && <div className={`max-h-32 overflow-y-auto pr-2`}>
                  {(data?.comment_history ?? []).map((comment: any, index: number) => commentHistory(comment, index))}
                </div>}
                {!token && <div>
                  <label className="text-sm text-default-500">Email<span className="text-danger">*</span></label>
                  <Input
                    type="email"
                    radius="sm"
                    size="sm"
                    placeholder="abc@example.com"
                    value={userEmail}
                    onValueChange={(val) => {
                      setUserEmail(val);
                      setEmailError('');
                    }}
                    isInvalid={!!emailError}
                    errorMessage={emailError}
                  />
                </div>}
                <div className="rounded-md border border-default-300 overflow-hidden">
                  {!isAgentFeedback && (
                    <AttachmentPreviewSection uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles} enableImage />
                  )}
                  <Textarea
                    id="comment-modal-textarea"
                    type="text"
                    size='sm'
                    isDisabled={isCommentFieldDisabled}
                    placeholder="Add comment"
                    onValueChange={(val) => {
                      setUserComment(val);
                      setCommentError('');
                    }}
                    value={currentUserComment}
                    minRows={7}
                    maxRows={8}
                    isInvalid={!!commentError}
                    errorMessage={commentError}
                    onPaste={!isAgentFeedback ? handlePaste : undefined}
                    endContent={
                      !isAgentFeedback && (
                        <CommentAttachmentButton addFiles={addFiles} disabled={isCommentFieldDisabled} />
                      )
                    }
                  />
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button id="cancel-modal-btn" radius="sm" variant="bordered" onPress={() => closeModal()}>
                {t('Cancel')}
              </Button>
              <Button id="comment-modal-btn" radius="sm" color="primary" onPress={() => saveMetadata()} isLoading={isFeedbackLoading || isGuestFeedbackLoading || isAgentFeedbackLoading} isDisabled={isUpdateButtonDisabled}>
                {t('Save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

};

export { CommentModal };